import { memo, useState } from 'react';
import styles from './Reward.scss';
import classNames from 'classnames';
import { DivTooltip, Interpolate } from '@wg/wows-react-uikit';
import { formatNumber, isEmptyObject, isMobileOrTabletWindow } from '~/utils/utils';
import Currency from '~/components/Currency/Currency';
import { t } from '~/utils/localization';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';
import { PresentationStyles, items } from '@wg/wows-entities/const';
import { getCompensationForPrimaryItem, getPrimaryItem, isEnabledTooltipFromItemByType, isHiddenAmountForBundle } from '~/utils/bundles';
import { getTooltipContainsContent } from '~/components/Tooltip/Tooltips';
import DefaultTooltip from '~/components/Tooltip/DefaultTooltip';
import OfferMark from '~/components/OfferMark/OfferMark';
import { GameRarity } from '~/types/rarity';
import { getSupportedVideo } from '~/utils/video';
import { RARITY_ANIMATION_MAP } from '~/utils/rarity';

export enum RewardTemplate {
    DEFAULT = 'default',
    MIDDLE = 'middle',
    BIG = 'big',
}

export interface IRewardItem {
    name: string;
    title: string;
    rarity?: GameRarity;
    items: IItemCommonData[];
    images: {
        medium: string;
        big: string;
        small: string;
    };
    label?: string;
    backgroundColor?: string;
    cashback?: {
        currency: string;
        amount: number;
    };
}

export interface IReward {
    template?: RewardTemplate;
    delay: number;
    reward: IRewardItem;
    compensatedItems: number[];
}

const isAvailableRarityAnimation = (type: string, rarity: GameRarity) => {
    if (isMobileOrTabletWindow) {
        return false;
    }

    if (!RARITY_ANIMATION_MAP[rarity]) {
        return false;
    }

    return [items.VEHICLES, items.CAMOUFLAGE, items.PERMOFLAGES, items.SKIN, items.MSKIN, items.STYLE].includes(type as items);
};

const Reward = ({ template, delay, reward, compensatedItems }: IReward) => {
    const [isVisibleAnimation, setIsVisibleAnimation] = useState(false);
    const style = { '--delay': `${delay}s` } as React.CSSProperties;

    let tooltip = getTooltipContainsContent(reward.items);
    if (reward.items.length === 1 && isEnabledTooltipFromItemByType(reward.items[0].type)) {
        const item = reward.items[0];
        if (item.customTitle || item.customDescription) {
            tooltip = <DefaultTooltip text={item.customDescription || item.customTitle} />;
        } else {
            tooltip = (
                <WoWSEntity
                    type={item.type}
                    amount={item.amount}
                    id={item.identifier}
                    presentation={{
                        style: PresentationStyles.TOOLTIP_WIDGET,
                        withTooltip: true,
                    }}
                    customisation={item.customisation}
                />
            );
        }
    }

    const classesWrapper = classNames(styles.wrapper, styles[template]);

    const primaryItem = getPrimaryItem(reward.items);
    const compensation = getCompensationForPrimaryItem(primaryItem);
    const isCompensated = compensatedItems?.includes(primaryItem?.identifier);
    const isAnimationAvailable = isAvailableRarityAnimation(primaryItem?.type, reward.rarity);
    const animationUrl = isAnimationAvailable ? getSupportedVideo(RARITY_ANIMATION_MAP[reward.rarity]) : null;

    const classesAnimation = classNames(styles.animation, {
        [styles.visible]: isVisibleAnimation,
    });

    const onLoadedData = () => {
        setIsVisibleAnimation(true);
    };

    return (
        <div className={classesWrapper} style={style}>
            <DivTooltip tooltipBody={tooltip} className={styles.tooltipWrapper}>
                <div className={styles.animationWrapper}>
                    {animationUrl && (
                        <div className={classesAnimation}>
                            <video src={animationUrl} onLoadedData={onLoadedData} loop autoPlay />
                        </div>
                    )}
                    <img draggable={false} src={reward.images?.medium}></img>
                    {reward.items.length === 1 && !isHiddenAmountForBundle(reward.items) && reward.items[0].amount > 1 && (
                        <div className={styles.counterWrapper}>
                            <div className={styles.amount}>{formatNumber(reward.items[0].amount)}</div>
                        </div>
                    )}
                </div>
                <div className={styles.rewardInfo}>{primaryItem?.type === items.VEHICLES ? <WoWSEntity type={items.VEHICLES} id={primaryItem.identifier} /> : <span>{reward.title}</span>}</div>
            </DivTooltip>
            <div className={styles.additionalInfo}>
                {!isEmptyObject(reward.cashback) && (
                    <div className={styles.cashbackInfo}>
                        <span className={styles.plus}>+</span>
                        <WoWSEntity
                            type={reward.cashback.currency}
                            amount={reward.cashback.amount}
                            className={styles.currency}
                            presentation={{
                                renderWithoutFade: true,
                                iconPosition: 'left',
                                withTooltip: true,
                            }}
                        />
                    </div>
                )}
                {isCompensated && (
                    <Interpolate str={`${t('Компенсация')} %(currency)s`} currency={<Currency currency={compensation.currency} amount={compensation.amount} className={styles.currency} />} />
                )}
                <div className={styles.label}>{reward.label && <OfferMark label={reward.label} />}</div>
            </div>
        </div>
    );
};

export default memo(Reward);
